import { clearWebSession, getWebSession, storeWebSession } from './webSessionAuth';
import { clearNativeIosLinkedUser, getNativeIosLinkedUser, setNativeIosLinkedUser, type NativeIosLinkedUser } from './nativeApiRouting';
import { legacySnakeKey } from '../utils/legacyHostCompat';

export const ACCOUNT_CHANGED_EVENT = 'ziawellnav-account-changed';
export const SAVED_ACCOUNTS_KEY = 'ziawellnav_saved_accounts_v1';
export const SIGNED_OUT_KEY = 'ziawellnav_explicitly_signed_out';
const LEGACY_ID = legacySnakeKey('user_id');
export type AccountRole = 'operator' | 'contractor' | 'customer';
export type AccountUser = {
  id: string; email?: string; first_name?: string; last_name?: string; username?: string; photo_url?: string;
};
export type SavedAccount = {
  user: AccountUser; role: AccountRole; token: string; expiresAt: string;
  provider: 'web' | 'native'; nativeUser?: NativeIosLinkedUser;
};
let memoryAccounts: SavedAccount[] = [];
let memoryOnly = false;

function parseUser(value: unknown): AccountUser | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const source = raw.user && typeof raw.user === 'object' ? raw.user as Record<string, unknown> : raw;
  const id = String(source.id || '').trim();
  if (!id) return null;
  const str = (key: string, alias?: string) => String(source[key] || (alias ? source[alias] : '') || '').trim() || undefined;
  return { id, email: str('email'), first_name: str('first_name', 'firstName'), last_name: str('last_name', 'lastName'), username: str('username'), photo_url: str('photo_url', 'photoUrl') };
}
export function readAccountUser(): AccountUser | null {
  const active = activeSavedAccount();
  if (active) return active.user;
  try { return parseUser(JSON.parse(sessionStorage.getItem('tg_auth_user') || 'null')); } catch { return null; }
}
export function isExplicitlySignedOut(): boolean {
  try { return sessionStorage.getItem(SIGNED_OUT_KEY) === '1'; } catch { return false; }
}
export function isAccountExpired(account: SavedAccount): boolean {
  return (account.provider === 'web' && !account.expiresAt) || Boolean(account.expiresAt && (!Number.isFinite(Date.parse(account.expiresAt)) || Date.parse(account.expiresAt) <= Date.now()));
}
export function savedAccounts(): SavedAccount[] {
  if (memoryOnly) return memoryAccounts;
  try {
    const rows: unknown = JSON.parse(localStorage.getItem(SAVED_ACCOUNTS_KEY) || '[]');
    if (!Array.isArray(rows)) return [];
    return rows.filter((row): row is SavedAccount => !!parseUser(row?.user) && typeof row?.token === 'string' && !!row.token && ['web', 'native'].includes(row.provider));
  } catch { return memoryOnly ? memoryAccounts : []; }
}
function writeAccounts(accounts: SavedAccount[]) {
  memoryAccounts = accounts;
  try { localStorage.setItem(SAVED_ACCOUNTS_KEY, JSON.stringify(accounts)); memoryOnly = false; }
  catch { memoryOnly = true; }
}
function saveAccount(account: SavedAccount) {
  writeAccounts([...savedAccounts().filter(row => row.user.id !== account.user.id), account]);
}
export function activeSavedAccount(): SavedAccount | null {
  const session = getWebSession();
  if (session) return savedAccounts().find(row => row.provider === 'web' && row.token === session.token) || null;
  const native = getNativeIosLinkedUser();
  return native?.sessionToken ? savedAccounts().find(row => row.provider === 'native' && row.token === native.sessionToken) || null : null;
}
/** Capture an existing verified session before adding another account. */
export function rememberCurrentAccount() {
  const session = getWebSession();
  const user = activeSavedAccount()?.user || readAccountUser();
  if (user) {
    try {
      const legacy = localStorage.getItem('ziawellnav_worker_profile_card');
      if (legacy) {
        const key = `ziawellnav_worker_profile_card:${encodeURIComponent(user.id)}`;
        if (!localStorage.getItem(key)) localStorage.setItem(key, legacy);
        localStorage.removeItem('ziawellnav_worker_profile_card');
      }
    } catch { /* Best-effort migration of this account’s saved profile. */ }
  }
  if (session && user && !activeSavedAccount()) {
    saveAccount({ user, token: session.token, expiresAt: session.expiresAt, role: 'customer', provider: 'web' });
  } else if (!session) {
    const native = getNativeIosLinkedUser();
    if (native?.sessionToken && !activeSavedAccount()) saveAccount({
      user: { id: native.userId, first_name: native.companyName || native.username || 'Account' },
      role: 'customer', provider: 'native', token: native.sessionToken, expiresAt: '', nativeUser: native,
    });
  }
}
function writeIdentity(user: AccountUser | null) {
  try {
    if (user) {
      sessionStorage.setItem('tg_auth_user', JSON.stringify(user));
      sessionStorage.removeItem(SIGNED_OUT_KEY);
      localStorage.setItem(LEGACY_ID, user.id);
    } else {
      sessionStorage.removeItem('tg_auth_user');
      sessionStorage.setItem(SIGNED_OUT_KEY, '1');
      localStorage.removeItem(LEGACY_ID);
    }
  } catch { /* The verified token remains the authority when storage is blocked. */ }
}
function resetAccountPreferences() {
  // These are account choices, not device appearance, map downloads, or saved work.
  for (const key of ['worker_type', 'ziawellnav_worker_profile_business_address', 'zwn-open-bids-profile']) {
    try { localStorage.removeItem(key); } catch { /* unavailable */ }
  }
}
function changed(signedIn: boolean) {
  window.dispatchEvent(new CustomEvent(ACCOUNT_CHANGED_EVENT));
  window.dispatchEvent(new CustomEvent(signedIn ? 'ziawellnav-bid-board-session' : 'ziawellnav-bid-board-sign-out'));
}
export function completeAccountSignIn(input: { user: unknown; token: string; expiresAt: string; role?: AccountRole }) {
  const user = parseUser(input.user);
  if (!user || !input.token) throw new Error('Sign-in did not return a verified account.');
  const account: SavedAccount = { user, token: input.token, expiresAt: input.expiresAt, role: input.role || savedAccounts().find(row => row.user.id === user.id)?.role || 'customer', provider: 'web' };
  if (isAccountExpired(account)) throw new Error('This sign-in has expired. Please sign in again.');
  rememberCurrentAccount();
  resetAccountPreferences();
  clearNativeIosLinkedUser();
  saveAccount(account);
  storeWebSession(account.token, account.expiresAt);
  writeIdentity(user);
  changed(true);
}
export function switchAccount(userId: string) {
  rememberCurrentAccount();
  const account = savedAccounts().find(row => row.user.id === userId);
  if (!account || isAccountExpired(account)) throw new Error('This account needs a fresh sign-in. Use Add account to sign in again.');
  if (account.provider === 'native' && !account.nativeUser) throw new Error('Please sign in to this account again.');
  resetAccountPreferences();
  clearWebSession();
  clearNativeIosLinkedUser();
  if (account.provider === 'web') storeWebSession(account.token, account.expiresAt);
  else if (account.nativeUser) setNativeIosLinkedUser(account.nativeUser);
  else throw new Error('Please sign in to this account again.');
  writeIdentity(account.user);
  changed(true);
}
export function signOutAccount(all = false) {
  const active = activeSavedAccount();
  const id = active?.user.id || readAccountUser()?.id || getNativeIosLinkedUser()?.userId;
  writeAccounts(all ? [] : savedAccounts().filter(row => row.user.id !== id));
  clearWebSession();
  clearNativeIosLinkedUser();
  resetAccountPreferences();
  writeIdentity(null);
  changed(false);
}
/** Synchronize identity when another tab changes the active verified session. */
export function synchronizeAccountIdentity(clearMissing = false) {
  const account = activeSavedAccount();
  if (account) writeIdentity(account.user);
  else if (clearMissing && !getWebSession() && !getNativeIosLinkedUser()?.sessionToken) writeIdentity(null);
}

export function accountStorageKey(base: string): string {
  const id = isExplicitlySignedOut() ? null : (activeSavedAccount()?.user.id || readAccountUser()?.id || getNativeIosLinkedUser()?.userId);
  return `${base}:${encodeURIComponent(id || 'guest')}`;
}
